// src/lib/api.ts — Stable Before Dev - 21-10-2025-V04 12:00 PM
import axios, { AxiosInstance, AxiosRequestConfig, AxiosHeaders } from "axios";

/* -------------------------------------------------------
 * قاعدة الـ API
 * ----------------------------------------------------- */
export function apiBase(): string {
  // 0) في وضع تطوير Next.js (منفذ 3000): استخدم نفس المضيف على منفذ 8000 دائماً
  // لتفادي الالتباس مع api_base المحفوظ في localStorage أو أي Proxy خارجي قديم.
  if (typeof window !== "undefined" && window.location.port === "3000") {
    const h = window.location.hostname;
    return `http://${h}:8000`;
  }

  // 1) من .env
  const env = (process.env.NEXT_PUBLIC_API_BASE || "").trim();
  if (env) return env.replace(/\/+$/, "");
  // 2) من localStorage (إن وُجد)
  if (typeof window !== "undefined") {
    try {
      const saved = (window.localStorage.getItem("api_base") || "").trim();
      if (saved) return saved.replace(/\/+$/, "");
    } catch { /* ignore */ }
  }

  
  // 3) تلقائي:
  //    - HTTPS: استخدم نفس origin (nginx يوجّه /api للـ backend)
  //    - HTTP: استخدم hostname مع بورت 8000
  if (typeof window !== "undefined") {
    if (window.location.protocol === "https:") {
      return window.location.origin + "/_be";
    }
    const h = window.location.hostname;
    return `http://${h}:8000`;
  }
  
  // 4) fallback للسيرفر
  return "http://localhost:8000";
}


// ** مُساعد صغير لاختيار المسار النهائي بشكل سليم (اختياري). */
export function apiPath(path: string): string {
  const base = apiBase();
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${base}${clean}`;
}



/* -------------------------------------------------------
 * كوكي
 * ----------------------------------------------------- */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  // نهرب كل محارف الـ RegExp الخاصة
  const safeName = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1");
  const m = document.cookie.match(new RegExp("(?:^|; )" + safeName + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

export function setCookie(name: string, value: string, days = 7) {
  if (typeof document === "undefined") return;
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "expires=" + d.toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
}

export function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

/* -------------------------------------------------------
 * توكن المصادقة (JWT/Bearer)
 * - نستخدم localStorage + Cookie "access_token"
 * ----------------------------------------------------- */
const TOKEN_KEY = "access_token";

/** احصل على التوكن من LS أولًا ثم الكوكي. */
export function getToken(): string | null {
  try {
    if (typeof localStorage !== "undefined") {
      const ls = localStorage.getItem(TOKEN_KEY);
      if (ls) return ls;
    }
  } catch { /* ignore */ }
  return getCookie(TOKEN_KEY);
}

/** خزّن التوكن في LS + Cookie. إن مرّرت null سيتم الحذف. */
export function setToken(token: string | null, cookieDays = 7) {
  try {
    if (typeof localStorage !== "undefined") {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    }
  } catch { /* ignore */ }
  if (token) setCookie(TOKEN_KEY, token, cookieDays);
  else deleteCookie(TOKEN_KEY);
}

/** احذف كل آثار التوكن. */
export function clearToken() {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch { /* ignore */ }
  deleteCookie(TOKEN_KEY);
}


// ** رؤوس المصادقة الجاهزة للإرسال. */
export function getAuthHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* -------------------------------------------------------
 * Axios instance افتراضي + تصدير افتراضي
 * ----------------------------------------------------- */
export const api: AxiosInstance = axios.create({
  baseURL: apiBase(),
  withCredentials: true, // نحتاج الكوكي HttpOnly
  headers: {
    Accept: "application/json",
  },
});

/** تحقّق هل لدى headers ترويسة Authorization معيّنة مسبقًا (تدعم AxiosHeaders). */
function hasAuthorizationHeader(h: any): boolean {
  if (!h) return false;
  // AxiosHeaders يملك get()
  if (typeof h.get === "function") {
    return !!h.get("Authorization");
  }
  // كائن عادي
  return "Authorization" in h;
}

/** أضف/ادمج رؤوسًا بأمان (تدعم AxiosHeaders). */
function mergeHeaders(target: any, extra: Record<string, string>) {
  if (!target) return extra;
  if (target instanceof AxiosHeaders || typeof target.set === "function") {
    for (const [k, v] of Object.entries(extra)) {
      target.set(k, v);
    }
    return target;
  }
  return Object.assign(target, extra);
}

// ضَمِّن Authorization تلقائيًا إن لم يكن مُرسلًا + زامن baseURL في كل طلب
api.interceptors.request.use((config) => {
  // أعِد تقييم baseURL دومًا لأن api_base قد يتغيّر وقت التشغيل
  const currentBase = apiBase();
  if (currentBase) {
    // أزل الشرطات المائلة الختامية للتناسق
    const normalized = currentBase.replace(/\/+$/, "");
    if (config.baseURL !== normalized) {
      config.baseURL = normalized;
    }
  }

  // رؤوس
  if (!config.headers) config.headers = new AxiosHeaders();
  if (!hasAuthorizationHeader(config.headers)) {
    mergeHeaders(config.headers, getAuthHeaders());
  }

  // تأكد من withCredentials
  (config as AxiosRequestConfig).withCredentials = true;
  return config;
});

// ── Response interceptor: إعادة التوجيه عند 401/403 ──
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      // توكن منتهي أو غير صالح → تسجيل خروج + توجيه لصفحة الدخول
      if (typeof window !== "undefined") {
        clearToken();
        // تجنب حلقة لا نهائية: لا تُعيد التوجيه إذا كنا بالفعل في صفحة الدخول
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/* -------------------------------------------------------
 * مُساعد fetch JSON (اختياري)
 * ----------------------------------------------------- */
export async function jsonFetch<T = any>(
  path: string,
  init: RequestInit & { json?: any } = {}
): Promise<{ ok: boolean; status: number; data: T | any; response: Response }> {
  const url = apiPath(path);
  const headers: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(init.headers || {}),
    ...getAuthHeaders(),
  };
  const opts: RequestInit = {
    credentials: "include",
    ...init,
    headers,
    body:
      init.json !== undefined
        ? JSON.stringify(init.json)
        : init.body !== undefined
        ? init.body
        : undefined,
  };
  const response = await fetch(url, opts);
  const ct = response.headers.get("content-type") || "";
  const data = ct.includes("application/json")
    ? await response.json().catch(() => ({}))
    : await response.text().catch(() => "");
  return { ok: response.ok, status: response.status, data, response };
}

/* -------------------------------------------------------
 * مُساعدات صغيرة إضافية (اختيارية)
 * ----------------------------------------------------- */

/** تغيير الـ base مؤقتًا وقت التشغيل (ويُحفظ محليًا). */
export function setApiBaseRuntime(base: string) {
  const clean = (base || "").trim().replace(/\/+$/, "");
  if (typeof window !== "undefined") {
    try {
      if (clean) localStorage.setItem("api_base", clean);
      else localStorage.removeItem("api_base");
    } catch { /* ignore */ }
  }
  // عدّل axios مباشرةً
  (api.defaults as any).baseURL = clean || apiBase();
}

/** اختبار سريع للاتصال/المصادقة. */
export async function pingApi(): Promise<{ ok: boolean; status: number }> {
  try {
    const { response, status, ok } = await jsonFetch("/ping");
    return { ok, status: status || response.status };
  } catch {
    return { ok: false, status: 0 };
  }
}
